import { i18n } from '@/i18n';
import { landingSpotlightJa } from '@/i18n/landing-spotlight/ja.js';
import { markdownToSpotlightBlocks } from '@/lib/spotlightMarkdown';
import type {
  SpotlightListConfig,
  SpotlightDetailPost,
  SpotlightListPost,
} from '@/composables/useSpotlight';

type SpotlightJaArticles = typeof landingSpotlightJa.articles;
type SpotlightJaArticle = SpotlightJaArticles[keyof SpotlightJaArticles];

const spotlightBodyJa: Record<string, () => Promise<{ default: string }>> = {
  'gpt-image-2-guide-and-best-alternatives': () =>
    import('@/content/spotlight/ja/gpt-image-2-guide-and-best-alternatives.md?raw'),
  'claude-design-guide-and-best-alternatives': () =>
    import('@/content/spotlight/ja/claude-design-guide-and-best-alternatives.md?raw'),
  'gemini-3-5-guide': () => import('@/content/spotlight/ja/gemini-3-5-guide.md?raw'),
};

export function isSpotlightJaLocale(): boolean {
  return i18n.locale === 'ja';
}

function getJaArticle(slug: string): SpotlightJaArticle | null {
  if (!isSpotlightJaLocale()) return null;
  return landingSpotlightJa.articles[slug as keyof typeof landingSpotlightJa.articles] ?? null;
}

function faqAnswerHtml(answer: string): string {
  const trimmed = answer.trim();
  if (!trimmed) return '';
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return `<p>${trimmed.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
}

function applyJaArticleFields<T extends SpotlightListPost>(post: T, ja: SpotlightJaArticle): T {
  return {
    ...post,
    title: ja.title || post.title,
    excerpt: ja.excerpt || post.excerpt,
    imageAlt: ja.imageAlt || post.imageAlt,
    category: ja.category || post.category,
    categories: ja.category ? [{ title: ja.category }] : post.categories,
    seoTitle: ja.seoTitle || post.seoTitle,
    seoDescription: ja.seoDescription || post.seoDescription,
    keywords: ja.keywords?.length ? ja.keywords : post.keywords,
  };
}

export function applySpotlightListConfigLocale(config: SpotlightListConfig): SpotlightListConfig {
  if (!isSpotlightJaLocale()) return config;
  const ja = landingSpotlightJa.list;
  return {
    ...config,
    seoTitle: ja.seoTitle || config.seoTitle,
    seoDescription: ja.seoDescription || config.seoDescription,
    title: ja.title || config.title,
    subtitle: ja.subtitle || config.subtitle,
    faqTitle: ja.faqTitle || config.faqTitle,
    faqItems: ja.faqItems?.length ? ja.faqItems : config.faqItems,
  };
}

export function applySpotlightPostsListLocale(posts: SpotlightListPost[]): SpotlightListPost[] {
  if (!isSpotlightJaLocale()) return posts;
  return posts.map((post) => {
    const ja = getJaArticle(post.slug);
    return ja ? applyJaArticleFields(post, ja) : post;
  });
}

export async function applySpotlightPostDetailLocale(
  post: SpotlightDetailPost
): Promise<SpotlightDetailPost> {
  if (!isSpotlightJaLocale()) return post;
  const ja = getJaArticle(post.slug);
  if (!ja) return post;

  const loader = spotlightBodyJa[post.slug];
  let bodyContent = post.bodyContent || [];
  let bodyPortableText = post.bodyPortableText;

  if (loader) {
    const mod = await loader();
    bodyContent = markdownToSpotlightBlocks(mod.default);
    bodyPortableText = null;
  }

  const localized = applyJaArticleFields(post, ja);

  return {
    ...localized,
    faq: ja.faq?.length ? ja.faq : localized.faq,
    bodyContent,
    bodyPortableText,
    relatedPosts: applySpotlightPostsListLocale(
      localized.relatedPosts || []
    ) as SpotlightDetailPost['relatedPosts'],
  };
}

export function getSpotlightListFaqItems(): Array<{ question: string; answer: string }> {
  if (!isSpotlightJaLocale()) return [];
  return landingSpotlightJa.list.faqItems || [];
}

export { faqAnswerHtml };
